import { CopyOutlined } from "@ant-design/icons";
import { Flex, InputNumber } from "antd";
import type { InputNumberRef } from "rc-input-number";
import { FC, memo, SyntheticEvent, useCallback, useRef, useState } from "react";
import {
    MarketProductProgress,
    ProductOrderItem,
    ProductPlan,
} from "tm-shared/types";

import { IgnoreRemainingConfirm } from "@/components/Modals/IgnoreRemainingConfirm";
import { RemainingsWarningModal } from "@/components/Modals/RemainingsWarning";
import { useAppDispatch } from "@/hooks/redux";
import { useInViewObserver } from "@/hooks/useInViewObserver";
import { useMultiTap } from "@/hooks/useMultiTap";
import { IgnoreStockActions } from "@/pages/WorkPlan/Tasks/components/GeneralProductCard/components/IgnoreStockActions";
import { ProductHeader } from "@/pages/WorkPlan/Tasks/components/GeneralProductCard/components/ProductHeader";
import { addSaleIgnoredRemainingsProduct } from "@/store/slices/progress";
import { copyObjectToClipboard } from "@/utils/dom/copyObjectToClipboard";
import { handleFocus } from "@/utils/dom/handleFocus";

import { ProductKpiInfo } from "./components/ProductKpiInfo";
import { ProductTags } from "./components/ProductTags";
import {
    StyledProductBarcode,
    StyledProductInfoText,
    StyledProductItem,
    StyledProductItemWrapper,
    StyledRemainingCount,
} from "./GeneralProductCard.styled";

interface IGeneralProductCard {
    name: string;
    barcode: string;
    marketId: string;
    taskId: string;
    price: number;
    oldPrice: number;
    itemsLeft?: number;
    hasSelectedPaymentForm?: boolean;
    value: number;
    isRemainingIgnored: boolean;
    isProductRemainingIgnored: boolean;
    isPersonalPrice?: boolean;
    isNew?: boolean;
    isSpecialTask?: "default" | "dropout" | null;
    isMML?: boolean;
    isLast?: boolean;
    productPlan?: ProductPlan | null;
    additionalPlan?: ProductPlan | null;
    productFact?: MarketProductProgress | null;
    onInView?: () => void;
    onCountChange: (product: ProductOrderItem) => void;
}

export const GeneralProductCardComponent: FC<IGeneralProductCard> = ({
    name,
    barcode,
    price,
    oldPrice,
    itemsLeft,
    hasSelectedPaymentForm,
    value,
    isRemainingIgnored,
    isProductRemainingIgnored,
    isPersonalPrice,
    marketId,
    isLast,
    isNew,
    isSpecialTask = null,
    isMML,
    productPlan,
    additionalPlan,
    productFact,
    taskId,
    onInView,
    onCountChange,
}) => {
    const dispatch = useAppDispatch();

    const cardRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<InputNumberRef>(null);

    const [pendingValue, setPendingValue] = useState<number | null>(null);
    const [showIgnoreWrapper, setShowIgnoreWrapper] = useState(false);
    const [remainingsModal, setRemainingsModal] = useState<
        "warning" | "ignore" | null
    >(null);

    const isOrderRemainingIgnore = isRemainingIgnored;
    const isItemRemainingIgnore = isProductRemainingIgnored;
    const isCountDisabled =
        ((!isItemRemainingIgnore && !itemsLeft) || !price) && !value;

    const maxValue = isItemRemainingIgnore ? Infinity : itemsLeft ?? 0;

    const productTags = {
        isNew,
        isSpecialTask,
        isMML,
        productPlan: Boolean(productPlan),
        additionalPlan: Boolean(additionalPlan),
    };

    const handleDoubleClick = () => {
        if (!isItemRemainingIgnore) setShowIgnoreWrapper(true);
    };

    const handleTap = useMultiTap(handleDoubleClick);

    const handleCopyProductInfo = (event: SyntheticEvent) => {
        event.stopPropagation();
        copyObjectToClipboard({
            barcode,
            name,
            marketId,
            price,
            oldPrice,
            itemsLeft,
            isPersonalPrice,
        });
    };

    useInViewObserver(cardRef, () => {
        if (isLast) onInView?.();
    });

    const confirmIgnoreLocally = useCallback(() => {
        dispatch(addSaleIgnoredRemainingsProduct({ taskId, barcode }));
        if (pendingValue != null) {
            onCountChange({ barcode, count: pendingValue, name, price });
        }
        setPendingValue(null);
        setRemainingsModal(null);
        setShowIgnoreWrapper(false);
    }, [dispatch, taskId, barcode, pendingValue, onCountChange, name, price]);

    const handleChange = useCallback(
        (val: number | null) => {
            const numeric = val ?? 0;

            if (numeric > maxValue) {
                setPendingValue(numeric);
                setRemainingsModal("warning");
                return;
            }

            onCountChange({ barcode, count: numeric, name, price });
        },
        [maxValue, barcode, onCountChange, name, price],
    );

    const handleConfirmRemainingsWarning = useCallback(() => {
        if (isOrderRemainingIgnore) {
            confirmIgnoreLocally();
        } else {
            setRemainingsModal("ignore");
        }
    }, [confirmIgnoreLocally, isOrderRemainingIgnore]);

    const handleCancelModal = useCallback(() => {
        setPendingValue(null);
        setRemainingsModal(null);

        if (inputRef.current) {
            inputRef.current.focus();
        }
    }, []);

    const handleIgnoreClick = useCallback(() => {
        if (isOrderRemainingIgnore) {
            confirmIgnoreLocally();
        } else {
            setRemainingsModal("ignore");
        }
    }, [confirmIgnoreLocally, isOrderRemainingIgnore]);

    const displayValue = pendingValue ?? value ?? 0;

    return (
        <>
            <div ref={cardRef} style={{ width: "100%" }}>
                <StyledProductItemWrapper>
                    <StyledProductItem>
                        <ProductTags tags={productTags} />

                        <ProductHeader
                            name={name}
                            barcode={barcode}
                            price={price}
                            oldPrice={oldPrice}
                            isPersonalPrice={isPersonalPrice}
                        />

                        <Flex justify="space-between">
                            <Flex vertical align="flex-start" gap="4px">
                                <StyledProductBarcode
                                    onClick={handleCopyProductInfo}
                                >
                                    Штрихкод: {barcode} <CopyOutlined />
                                </StyledProductBarcode>

                                <StyledProductInfoText
                                    onTouchStart={handleTap}
                                    style={{ touchAction: "manipulation" }}
                                >
                                    Залишок:{" "}
                                    <StyledRemainingCount
                                        $isInfinity={isItemRemainingIgnore}
                                    >
                                        {isItemRemainingIgnore
                                            ? "∞"
                                            : itemsLeft ?? "0"}
                                    </StyledRemainingCount>
                                </StyledProductInfoText>

                                {(productPlan ||
                                    additionalPlan ||
                                    productFact) && (
                                    <ProductKpiInfo
                                        plan={productPlan}
                                        fact={productFact}
                                        onTouchStart={handleTap}
                                        additionalPlan={additionalPlan}
                                    />
                                )}
                            </Flex>

                            {hasSelectedPaymentForm && (
                                <div
                                    onTouchStart={handleTap}
                                    style={{ touchAction: "manipulation" }}
                                >
                                    <InputNumber
                                        ref={inputRef}
                                        size="large"
                                        min={0}
                                        controls
                                        onFocus={(event) => {
                                            handleFocus(event);
                                            event.target.inputMode = "numeric";
                                        }}
                                        value={displayValue}
                                        inputMode="numeric"
                                        parser={(value) =>
                                            value
                                                ? +value.replace(/\D/g, "")
                                                : 0
                                        }
                                        disabled={isCountDisabled}
                                        onChange={handleChange}
                                    />
                                </div>
                            )}
                        </Flex>

                        {showIgnoreWrapper && (
                            <IgnoreStockActions
                                onIgnoreClick={handleIgnoreClick}
                            />
                        )}
                    </StyledProductItem>
                </StyledProductItemWrapper>
            </div>

            <IgnoreRemainingConfirm
                modalKey={barcode}
                isModalOpened={remainingsModal === "ignore"}
                confirmRemaining={confirmIgnoreLocally}
                cancelRemaining={handleCancelModal}
            />

            <RemainingsWarningModal
                open={remainingsModal === "warning"}
                onClose={handleCancelModal}
                onConfirm={handleConfirmRemainingsWarning}
                productName={name}
                left={itemsLeft ?? 0}
                input={pendingValue}
            />
        </>
    );
};

export const GeneralProductCard = memo(GeneralProductCardComponent);
